import * as _ from 'lodash';
import { format } from 'app/shared/StatFormatting';
import Constants from 'app/shared/utilities/constant';
import { VisibleRadio } from './visible_radio';

/**
 * If a radio stat has any of these fields it is for an LTE client
 * @type {string[]}
 */
const LTE_CLIENT_FIELDS = ['avg_rsrp', 'avg_rsrq', 'avg_sinr'];

const RADIO_FIELDS = ['avg_rssi', 'channel', 'tx_power', 'channel_utilisation'];

// Tiered list of stat types.
// LTE stats are a sub set of wifi stats
// WIFI stats are a sub set of generic stats
export enum InterfaceStatType {
    LTE = 'LTE', // Has the special fields required to be an LTE stat.
    FIVE_G = '5G', // Has the special fields required to be an 5G stat.
    WIFI = 'WIFI', // Has the special fields required to be a radio stat
    PTP = 'PTP', // Has the special fields required to be a ptp stat
    PTMP = 'PTMP', // Has the special fields required to be a ptmp stat
    GENERIC = 'GENERIC', // Has only the most generic fields to be a general network interface stat
}

/**
 * Wraps a radio stat data structure fresh from the API.
 *
 * You will notice that the property names on this class do not follow javascript conventions.
 * Using snake case brings this stat more into line with dynamic stats so we can treat them
 * in a similar fashion when using the IMSVisualiser chain of objects.
 *
 * I decided against using dual getters, i.e. avg_rssi and averageRSSI because the old getters
 * was mostly used when using the IMSVisualiser chain of objects and the infobox. The infobox implementation
 * for radio stats is a legacy implementation because of nested stats.
 *
 * This change gets us a tiny step closer to un-nesting radio stats from ip device stats.
 */
export class RadioStat {
    private readonly _radioStat;
    private readonly _visibleRadios: Array<VisibleRadio>;

    /**
     * @param radioStat a radioStat 'resource like' object. It's not actually a resource
     * because it is retrieved from the API as a child of an ip device stat.
     */
    constructor(radioStat) {
        this._radioStat = radioStat;
        this._visibleRadios =
            (this._radioStat.visible_radios &&
                this._radioStat.visible_radios.map(
                    (visibleRadio) => new VisibleRadio(visibleRadio),
                )) ||
            [];
    }

    get id(): number {
        return this._radioStat.id;
    }

    get visibleRadios(): Array<VisibleRadio> {
        return this._visibleRadios;
    }

    get radioId() {
        return this._radioStat.radio_id;
    }

    get radio_id() {
        return this._radioStat.radio_id;
    }

    get is_active() {
        return this._radioStat.is_active;
    }

    get avg_rssi() {
        return this._radioStat.avg_rssi;
    }

    get tx_power() {
        return this._radioStat.tx_power;
    }

    get noise() {
        return this._radioStat.noise;
    }

    get avg_rx_rate() {
        return this._radioStat.avg_rx_rate;
    }

    get avg_tx_rate() {
        return this._radioStat.avg_tx_rate;
    }

    get avg_rx_bps() {
        return this._radioStat.avg_rx_bps;
    }

    get avg_tx_bps() {
        return this._radioStat.avg_tx_bps;
    }

    get channel() {
        return this._radioStat.channel;
    }

    get statType(): InterfaceStatType {
        const radio = (window as any).services?.radioCollection?.get(this._radioStat.radio_id);
        if (radio) {
            const technology = (window as any).services?.radioTechnologyCollection?.get(
                radio.technologyId,
            );
            if (technology) {
                if (technology.type == 'lte') {
                    return InterfaceStatType.LTE;
                } else if (technology.type == '5g') {
                    return InterfaceStatType.FIVE_G;
                } else if (technology.type == 'wifi') {
                    return InterfaceStatType.WIFI;
                } else if (technology.type == 'ptp') {
                    return InterfaceStatType.PTP;
                } else if (technology.type == 'ptmp') {
                    return InterfaceStatType.PTMP;
                } else {
                    return InterfaceStatType.GENERIC;
                }
            }
        }

        // fallback
        if (LTE_CLIENT_FIELDS.some((field) => !_.isNil(this._radioStat[field]))) {
            return InterfaceStatType.LTE;
        } else if (RADIO_FIELDS.some((field) => !_.isNil(this._radioStat[field]))) {
            return InterfaceStatType.WIFI;
        } else {
            return InterfaceStatType.GENERIC;
        }
    }

    get snr() {
        if (this._radioStat.avg_rssi === null || isNaN(this._radioStat.avg_rssi)) {
            return null;
        }

        if (this._radioStat.noise === null || isNaN(this._radioStat.noise)) {
            return null;
        }

        // handle edge-case for noise being greater than RSSI (e.g. at Roy Hill)
        if (this._radioStat.noise > this._radioStat.avg_rssi) {
            return null;
        }

        return this._radioStat.avg_rssi - this._radioStat.noise;
    }

    get avg_rsrp() {
        return this._radioStat.avg_rsrp;
    }

    get avg_rsrq() {
        return this._radioStat.avg_rsrq;
    }

    get avg_sinr() {
        return this._radioStat.avg_sinr;
    }

    get channel_utilisation() {
        return this._radioStat.channel_utilisation;
    }

    formatted(throughputAsBits: boolean) {
        return {
            radioId: this.radioId,
            noise: format.noise(this.noise),
            channel: format.channel(this.channel),
            snr: format.snr(this.snr),
            visibleRadios: this.visibleRadios.map((visibleRadio) => visibleRadio.formatted),
            radio_id: this.radio_id,
            is_active: format.activeRadio(this.is_active),
            avg_rssi: format.rssi(this.avg_rssi),
            tx_power: format.signalStrength(this.tx_power),
            avg_rx_rate: format.megabitrate(this.avg_rx_rate, Constants.UNITS.BITRATE),
            avg_tx_rate: format.megabitrate(this.avg_tx_rate, Constants.UNITS.BITRATE),
            avg_rx_bps: throughputAsBits
                ? format.throughputBitrate(this.avg_rx_bps, Constants.UNITS.BITRATE)
                : format.byterate(this.avg_rx_bps, Constants.UNITS.BYTERATE),
            avg_tx_bps: throughputAsBits
                ? format.throughputBitrate(this.avg_tx_bps, Constants.UNITS.BITRATE)
                : format.byterate(this.avg_tx_bps, Constants.UNITS.BYTERATE),
            avg_rsrp: format.rsrp(this.avg_rsrp),
            avg_rsrq: format.rsrq(this.avg_rsrq),
            avg_sinr: format.sinr(this.avg_sinr),
            channel_utilisation: format.channelUtilisation(this.channel_utilisation),
        };
    }
}
